import { useChatStatus } from '@ai-sdk-tools/store';
import type { ChatRequestOptions, ChatStatus } from 'ai';
import { useEffect, useRef } from 'react';

import { normalizeChatUuid } from '../lib/notification-helpers';

export interface UseAutoResumeParams {
  /**
   * Whether auto-resume is enabled.
   * When true, the hook will automatically resume interrupted streams.
   */
  autoResume: boolean;
  /**
   * The active chat id. Existing chats probe the backend stream endpoint on
   * activation instead of inferring resumability from stale local state.
   */
  chatUuid: string;
  /**
   * Brand-new optimistic chats do not have a server stream to reconnect to yet.
   */
  isNewChat: boolean;
  /**
   * Backend stream id for the active stream, if known. When the server reports
   * a new stream id for the same chat, the probe runs again for that stream.
   */
  activeStreamId?: string | null;
  /**
   * Exact durable workflow this client is already attached to. This comes from
   * the response header, not from the separately polled chat marker.
   */
  attachedWorkflowRunId?: string;
  /**
   * When true, only probe while the server reports an active stream for the
   * chat. The durable v2 stream endpoint replays the latest finished turn
   * instead of returning 204, so an unconditional probe would replay the last
   * turn on every chat activation and duplicate the already-hydrated message.
   */
  requiresActiveStream?: boolean;
  /**
   * Function to resume the stream.
   * Should be called when auto-resume is enabled for an existing chat activation.
   */
  resumeStream: (options?: ChatRequestOptions) => void;
}

export function shouldAutoResume({
  activeStreamId,
  autoResume,
  isNewChat,
  requiresActiveStream,
  status,
}: {
  activeStreamId?: string | null;
  autoResume: boolean;
  isNewChat: boolean;
  requiresActiveStream?: boolean;
  status: ChatStatus;
}): boolean {
  if (!autoResume || isNewChat) {
    return false;
  }

  if (requiresActiveStream && !activeStreamId) {
    return false;
  }

  if (status !== 'ready') {
    return false;
  }

  return true;
}

export function getAutoResumeAttemptKey({
  activeStreamId,
  chatUuid,
  isNewChat,
}: {
  activeStreamId?: string | null;
  chatUuid: string;
  isNewChat: boolean;
}): string | null {
  if (isNewChat) {
    return null;
  }

  if (activeStreamId) {
    return `stream:${activeStreamId}`;
  }

  return `chat:${normalizeChatUuid(chatUuid)}`;
}

/**
 * Hook that automatically resumes interrupted chat streams.
 * Based on the Vercel AI Chatbot implementation: https://github.com/vercel/ai-chatbot/blob/d0b6f37aeee532b942245ee9d406312f79e2fabe/hooks/use-auto-resume.ts
 *
 * When autoResume is enabled for an existing chat, this hook probes the
 * backend stream endpoint once per activation and lets the server decide
 * whether there is a live stream to resume — a 204 response simply means
 * there is nothing to reconnect to. The probe is deduped per activation key
 * (chat id, or stream id when known); no client-side resumability state is
 * kept beyond the lifetime of this hook.
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const chat = useChat({ resume: true, ... });
 *   const { resumeStream } = chat; // or from useChatActions
 *
 *   useAutoResume({
 *     autoResume: true,
 *     chatUuid,
 *     isNewChat,
 *     resumeStream,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAutoResume({
  activeStreamId,
  attachedWorkflowRunId,
  autoResume,
  chatUuid,
  isNewChat,
  requiresActiveStream,
  resumeStream,
}: UseAutoResumeParams) {
  const status = useChatStatus();
  const attemptedKeysRef = useRef<{ chatKey: string; keys: Set<string> }>({ chatKey: '', keys: new Set() });

  // Attempted keys are scoped to the active chat so revisiting a chat probes
  // again, while key changes within one activation are deduped as a set: the
  // key can legitimately flip back to an earlier value (stream id arrives,
  // then clears after the turn), and a flip back must not probe again.
  const chatKey = normalizeChatUuid(chatUuid);
  useEffect(() => {
    if (attemptedKeysRef.current.chatKey !== chatKey) {
      attemptedKeysRef.current = { chatKey, keys: new Set() };
    }
  }, [chatKey]);

  const attemptKey = getAutoResumeAttemptKey({
    activeStreamId,
    chatUuid,
    isNewChat,
  });

  // Durable ownership comes from the exact response header. A different turn
  // can become active server-side while this client is still draining its
  // previous stream, so global submitted/streaming status cannot prove that the
  // polled activeStreamId belongs to this request.
  //
  // AI SDK backends (Benji) have no workflow header contract and use a
  // status-based guard. Their first render after a chat switch can reflect the
  // previous chat's global status, so only consecutive renders of the same chat count.
  const prevRenderChatKeyRef = useRef('');
  useEffect(() => {
    const isSameChatAsPreviousRender = prevRenderChatKeyRef.current === chatKey;
    prevRenderChatKeyRef.current = chatKey;

    if (requiresActiveStream) {
      if (activeStreamId && activeStreamId === attachedWorkflowRunId) {
        attemptedKeysRef.current.keys.add(`stream:${activeStreamId}`);
      }
      return;
    }

    if (isSameChatAsPreviousRender && (status === 'submitted' || status === 'streaming') && attemptKey) {
      attemptedKeysRef.current.keys.add(attemptKey);
    }
  }, [activeStreamId, attachedWorkflowRunId, attemptKey, chatKey, requiresActiveStream, status]);

  useEffect(() => {
    if (
      !shouldAutoResume({
        activeStreamId,
        autoResume,
        isNewChat,
        requiresActiveStream,
        status,
      })
    ) {
      return;
    }

    if (!attemptKey || attemptedKeysRef.current.keys.has(attemptKey)) {
      return;
    }

    attemptedKeysRef.current.keys.add(attemptKey);
    if (requiresActiveStream && activeStreamId) {
      resumeStream({ body: { workflowRunId: activeStreamId, startIndex: 0 } });
    } else {
      resumeStream();
    }
  }, [activeStreamId, attemptKey, autoResume, isNewChat, requiresActiveStream, resumeStream, status]);
}
